import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Phone,
  ArrowRight,
  Check,
  Repeat,
  Headset,
  ShoppingBag,
  Settings,
  Users,
  Briefcase,
  Network,
  TrendingUp,
  ShieldCheck,
  Zap,
  Building2,
  ExternalLink,
} from "lucide-react";
import SEOHead from "@/components/SEOHead";
import ResponsiveAccordion from "@/components/ResponsiveAccordion";
import PartnerEarningsCalculator from "@/components/PartnerEarningsCalculator";
import PartnerApplicationForm from "@/components/PartnerApplicationForm";
import analytics from "@/utils/analytics";
import { usePartnerViewed } from "@/utils/trackingHooks";

const PROD_URL = process.env.REACT_APP_PRODUCTION_URL || "https://candopayments.com";
const PATH = "/landing/partner-programme";
const CANONICAL = `${PROD_URL}${PATH}`;
const LOGO_URL = "https://cdn.candopayments.com/logos/cando_long_colour_web_transparent.svg";
const ZOHO_BOOKINGS_URL = "https://booknow.candopayments.com/portal-embed#/93421000005069155";

const META_TITLE = "Ecommerce Partner Programme UK";
const META_DESCRIPTION =
  "Earn recurring commission by referring UK ecommerce merchants to CanDo Payments. Built for agencies, developers, consultants and technology partners.";
const OG_DESCRIPTION =
  "Join the CanDo Payments Partner Programme and earn recurring commission from successful UK merchant referrals. Built for ecommerce agencies, developers and consultants.";
const TWITTER_DESCRIPTION =
  "Earn recurring revenue by referring UK merchants to CanDo Payments. Designed for ecommerce agencies, developers, consultants and technology partners.";

// ─── Content (spec §15 FAQ — single source for the visible accordion AND
// the FAQPage schema, so they can never drift) ──────────────────────────
const FAQS = [
  {
    q: "Who can join the CanDo Payments Partner Programme?",
    a: "The programme is designed for ecommerce agencies, web developers, consultants, SaaS providers, technology partners, freelance developers and business networks that can introduce UK merchants to CanDo Payments.",
  },
  {
    q: "Is there a joining fee?",
    a: "No. There is no joining fee to apply for the CanDo Payments Partner Programme.",
  },
  {
    q: "How do partner commissions work?",
    a: "Partners receive recurring commission from successful merchant referrals. Commission is payable on approved merchants who go live and continue processing through CanDo Payments.",
  },
  {
    q: "Do I need technical payment knowledge?",
    a: "No. CanDo Payments manages the payment setup, onboarding and merchant support. You do not need to manage the payment infrastructure yourself.",
  },
  {
    q: "Which types of merchants can I refer?",
    a: "Partners can refer suitable UK merchants that need payment processing, ecommerce payments, online payments, card machines or integrated payment support.",
  },
  {
    q: "Which ecommerce platforms can CanDo support?",
    a: "CanDo Payments can support merchants using popular ecommerce platforms including Shopify, WooCommerce, Magento, OpenCart, PrestaShop and BigCommerce, subject to merchant requirements.",
  },
  {
    q: "How quickly can merchants be onboarded?",
    a: "Onboarding times vary depending on the merchant, business type and requirements. CanDo Payments will assess each referral and guide the merchant through the process.",
  },
  {
    q: "What support do partners receive?",
    a: "Partners receive support from the CanDo Payments team, including help with proposals, merchant conversations, onboarding and ongoing account support.",
  },
  {
    q: "Will I have to support the client's payments after referral?",
    a: "No. CanDo manages the payment conversation, onboarding and ongoing payment support. Partners can stay involved commercially where helpful, but they do not need to become the client's payment support contact.",
  },
  {
    q: "Can I book a call before applying?",
    a: "Yes. If you would prefer to speak to the team first, you can book a discovery call using the Zoho Bookings form on this page.",
  },
];

const PLATFORMS = ["Shopify", "WooCommerce", "Magento", "OpenCart", "PrestaShop", "BigCommerce"];

// ─── JSON-LD graph (spec §Schema): Organization · WebSite · WebPage ·
// Service · FAQPage · BreadcrumbList. No Review/AggregateRating. ───────────
function buildSchemaGraph() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${PROD_URL}/#organization`,
        name: "CanDo Payments",
        url: PROD_URL,
        logo: "https://cdn.candopayments.com/logos/cando_square_colour_web_transparent.svg",
      },
      {
        "@type": "WebSite",
        "@id": `${PROD_URL}/#website`,
        url: PROD_URL,
        name: "CanDo Payments",
        publisher: { "@id": `${PROD_URL}/#organization` },
        inLanguage: "en-GB",
      },
      {
        "@type": "WebPage",
        "@id": `${CANONICAL}#webpage`,
        url: CANONICAL,
        name: META_TITLE,
        description: META_DESCRIPTION,
        isPartOf: { "@id": `${PROD_URL}/#website` },
        about: { "@id": `${PROD_URL}/#organization` },
        inLanguage: "en-GB",
      },
      {
        "@type": "Service",
        "@id": `${CANONICAL}#service`,
        name: "CanDo Payments Partner Programme",
        serviceType: "Payment processing partner programme",
        description:
          "Recurring-commission referral partnership for UK ecommerce agencies, developers, consultants and technology partners who introduce merchants needing payment processing.",
        url: CANONICAL,
        provider: { "@id": `${PROD_URL}/#organization` },
        areaServed: { "@type": "Country", name: "United Kingdom" },
        audience: [
          "Ecommerce agencies",
          "Web developers",
          "Ecommerce consultants",
          "SaaS providers",
          "Technology partners",
          "Freelance developers",
          "UK referral partners",
        ].map((name) => ({ "@type": "Audience", audienceType: name })),
      },
      {
        "@type": "FAQPage",
        "@id": `${CANONICAL}#faq`,
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${CANONICAL}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: PROD_URL },
          { "@type": "ListItem", position: 2, name: "Partner Programme", item: CANONICAL },
        ],
      },
    ],
  };
}

// ─── small presentational helpers ────────────────────────────────────────
function SectionHeading({ id, eyebrow, title, children, center = true }) {
  return (
    <div className={`${center ? "text-center max-w-3xl mx-auto" : "max-w-3xl"} mb-10`}>
      {eyebrow && (
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">{eyebrow}</p>
      )}
      <h2 id={id} className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight">
        {title}
      </h2>
      {children && <p className="text-base text-brand-grey leading-relaxed mt-4">{children}</p>}
    </div>
  );
}

function Card({ icon: Icon, title, children, testId }) {
  return (
    <div
      className="bg-white border border-brand-light-grey rounded-2xl p-6 shadow-sm h-full"
      data-testid={testId}
    >
      {Icon && (
        <div className="w-11 h-11 rounded-xl bg-brand-aqua-light flex items-center justify-center mb-4">
          <Icon size={20} className="text-brand-aqua-dark" />
        </div>
      )}
      {title && <h3 className="font-heading text-lg font-bold text-brand-black mb-2">{title}</h3>}
      <p className="text-sm text-brand-grey leading-relaxed">{children}</p>
    </div>
  );
}

function CheckPill({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white border border-brand-light-grey rounded-full px-3.5 py-1.5 text-sm text-brand-black">
      <Check size={14} className="text-brand-aqua flex-shrink-0" />{children}
    </span>
  );
}

// In-page anchor CTA that fires an analytics event before the native jump.
function AnchorCTA({ href, event, variant = "primary", className = "", children, testId }) {
  const base =
    variant === "primary"
      ? "cta-primary text-white"
      : "bg-white border-2 border-brand-aqua text-brand-aqua-dark hover:bg-brand-aqua-light";
  return (
    <a
      href={href}
      onClick={() => event && analytics.landingEvent(event)}
      className={`inline-flex items-center justify-center gap-2 font-semibold rounded-full px-6 py-3.5 text-sm transition-all ${base} ${className}`}
      data-testid={testId}
      data-measurement-owned={event ? "landing" : undefined}
    >
      {children}
    </a>
  );
}

export default function PartnerLandingPage() {
  usePartnerViewed("partner_programme_landing", { itemType: "partner_programme" });

  const calcRef = useRef(null);
  const formRef = useRef(null);
  const discoveryRef = useRef(null);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const [blockedBySection, setBlockedBySection] = useState(false);

  // Sticky mobile Apply CTA: appears once the user is past the hero, but is
  // suppressed whenever the calculator, application form, or discovery iframe
  // is on screen so it can never cover their controls (spec mobile constraint).
  useEffect(() => {
    const sections = [calcRef.current, formRef.current, discoveryRef.current].filter(Boolean);
    if (sections.length === 0) return undefined;
    const onScreen = new Set();
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) onScreen.add(e.target);
          else onScreen.delete(e.target);
        });
        setBlockedBySection(onScreen.size > 0);
      },
      { threshold: 0.05 },
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolledPastHero(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll-depth milestones (once each).
  useEffect(() => {
    const fired = new Set();
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100;
      [25, 50, 75, 90].forEach((mark) => {
        if (pct >= mark && !fired.has(mark)) {
          if (analytics.landingEvent(`partner_scroll_${mark}`)) fired.add(mark);
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Discovery section viewed (once).
  useEffect(() => {
    const el = discoveryRef.current;
    if (!el) return undefined;
    let fired = false;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !fired) {
            if (analytics.landingEvent("partner_discovery_section_view")) {
              fired = true;
              obs.disconnect();
            }
          }
        });
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleIframeLoad = useCallback(() => analytics.landingEvent("partner_zoho_iframe_load"), []);

  const showSticky = scrolledPastHero && !blockedBySection;

  return (
    <div className="bg-white min-h-screen">
      <SEOHead
        title={META_TITLE}
        description={META_DESCRIPTION}
        ogDescription={OG_DESCRIPTION}
        twitterDescription={TWITTER_DESCRIPTION}
        path={PATH}
        robots="index, follow"
        suppressBreadcrumb
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(buildSchemaGraph())}</script>
      </Helmet>

      {/* ── 1. Minimal PPC header ───────────────────────────────────── */}
      <header
        className="sticky top-0 z-40 bg-white/90 backdrop-blur-sm border-b border-brand-light-grey/60"
        data-testid="partner-landing-header"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" aria-label="CanDo Payments home">
            <img src={LOGO_URL} alt="CanDo Payments" width="1217" height="165" className="h-6 sm:h-7 w-auto" />
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <a
              href="tel:+448003682989"
              onClick={() => analytics.clickPhone()}
              className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-brand-aqua hover:text-brand-aqua-dark transition-colors"
              data-testid="partner-header-phone"
              data-measurement-owned="contact"
            >
              <Phone size={15} /> 0800 368 2989
            </a>
            <AnchorCTA
              href="#partner-application"
              event="partner_apply_click"
              className="!px-5 !py-2.5"
              testId="partner-header-apply"
            >
              Apply Now
            </AnchorCTA>
          </div>
        </div>
      </header>

      <main>
        {/* ── 2. Hero ──────────────────────────────────────────────── */}
        <section className="hero-gradient py-14 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-heading text-3xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight">
              Earn Recurring Revenue From Merchant Referrals
            </h1>
            <p className="text-base sm:text-lg text-brand-grey leading-relaxed mt-5 max-w-2xl mx-auto">
              Refer ecommerce, retail and service clients to CanDo Payments. We handle the payment conversation,
              onboarding and support while you earn ongoing commission from successful merchant referrals.
            </p>
            <p className="text-sm text-brand-grey leading-relaxed mt-3 max-w-2xl mx-auto">
              Built for ecommerce agencies, web developers, Shopify and WooCommerce specialists, consultants, SaaS
              providers and technology partners.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <AnchorCTA href="#partner-application" event="partner_apply_click" testId="hero-apply">
                Apply For The Partner Programme <ArrowRight size={16} />
              </AnchorCTA>
              <AnchorCTA
                href="#partner-discovery-call"
                event="partner_discovery_click"
                variant="secondary"
                testId="hero-discovery"
              >
                Book A Discovery Call
              </AnchorCTA>
            </div>
            <p className="text-xs text-brand-grey mt-5">
              No joining fee. No technical setup required. No payment support burden. UK-based partner support.
            </p>
          </div>
        </section>

        {/* ── 3. Trust bar ─────────────────────────────────────────── */}
        <section className="py-12 bg-white border-b border-brand-light-grey/60">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-center font-heading text-lg sm:text-xl font-bold text-brand-black mb-6">
              Built for ecommerce agencies, developers, consultants and UK business partners
            </h2>
            <div className="flex flex-wrap justify-center gap-2.5">
              {[
                "Recurring commission",
                "No payment support burden",
                "UK-based onboarding",
                "Shopify & WooCommerce support",
                "Fast merchant setup",
              ].map((p) => (
                <CheckPill key={p}>{p}</CheckPill>
              ))}
            </div>
          </div>
        </section>

        {/* ── 4. Quick Answer ──────────────────────────────────────── */}
        <section className="py-14 sm:py-16 bg-brand-off-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Quick Answer" center={false} />
            <p className="text-base text-brand-grey leading-relaxed -mt-6">
              CanDo Payments helps ecommerce agencies, web developers, consultants and technology partners earn recurring
              monthly income by referring UK merchants that need card payment processing, payment gateways, online
              payments and ecommerce integrations. Partners introduce suitable merchants, CanDo manages the payment
              conversation and onboarding, and partners earn commission from successful merchants who go live and
              continue processing.
            </p>
          </div>
        </section>

        {/* ── 5. At a Glance ───────────────────────────────────────── */}
        <section className="py-14 sm:py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="At a Glance" />
            <div className="bg-white border border-brand-light-grey rounded-2xl shadow-sm divide-y divide-brand-light-grey/70">
              {[
                ["Best for", "ecommerce agencies, web developers, consultants, SaaS providers and referral partners"],
                ["Primary opportunity", "recurring monthly commission from successful merchant referrals"],
                ["Merchant types", "ecommerce, retail, hospitality, service businesses and omnichannel merchants"],
                ["Platforms", "Shopify, WooCommerce, Magento, OpenCart, PrestaShop and BigCommerce"],
                ["Partner support", "UK-based team, proposal support, merchant onboarding and payment guidance"],
                ["Support burden", "CanDo manages the payment conversation, onboarding and merchant support."],
              ].map(([k, v]) => (
                <div key={k} className="flex flex-col sm:flex-row sm:gap-6 px-5 sm:px-6 py-3.5">
                  <span className="text-sm font-bold text-brand-black sm:w-52 flex-shrink-0">{k}</span>
                  <span className="text-sm text-brand-grey leading-relaxed">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 6. Why join ──────────────────────────────────────────── */}
        <section className="py-14 sm:py-20 bg-brand-off-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Turn Your Existing Client Relationships Into Recurring Revenue">
              Your clients already need reliable payment processing. Instead of recommending providers for free, the
              CanDo Payments Partner Programme helps you earn recurring commission every month your referred merchants
              continue processing through CanDo. Our team supports the commercial conversation, handles merchant
              onboarding and manages the payment setup, so you can stay focused on your clients without becoming payment
              support.
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <Card icon={Repeat} title="Earn Monthly Recurring Commission">
                Generate ongoing revenue from successful merchant referrals.
              </Card>
              <Card icon={TrendingUp} title="Increase Client Value">
                Help clients access payment solutions designed around their business needs.
              </Card>
              <Card icon={Headset} title="Dedicated Partner Support">
                Our team supports proposals, onboarding and merchant setup.
              </Card>
              <Card icon={Settings} title="No Technical Complexity">
                We manage the payment infrastructure so you can focus on your client relationships.
              </Card>
            </div>
          </div>
        </section>

        {/* ── 7. Built For Developers, Agencies & Client Advisers ──── */}
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Built For Developers, Agencies & Client Advisers">
              Whether you build websites, advise ecommerce clients or support businesses with technology decisions, the
              Partner Programme helps you turn payment recommendations into recurring revenue without taking on payment
              support yourself.
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Card icon={Settings} title="For Web Developers">
                Recommend CanDo when clients need gateways, online payments or card processing support, without becoming
                responsible for payment onboarding or support.
              </Card>
              <Card icon={ShoppingBag} title="For Ecommerce Agencies">
                Add a recurring revenue stream to ecommerce projects while helping clients access payment solutions
                suited to their store, platform and trading model.
              </Card>
              <Card icon={Briefcase} title="For Consultants">
                Help clients review payment costs and access suitable processing options through a UK-based payments
                team.
              </Card>
              <Card icon={Network} title="For Technology Partners">
                Create more value from existing merchant relationships and referral networks without adding complex
                payment infrastructure to your own service.
              </Card>
            </div>
          </div>
        </section>

        {/* ── 7b. Typical Partner Use Cases ────────────────────────── */}
        <section className="py-14 sm:py-20 bg-white border-t border-brand-light-grey/60">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Typical Partner Use Cases">
              The Partner Programme is designed for common client moments where businesses need payment guidance,
              platform support or a better route to taking payments.
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <Card title="WooCommerce Payment Gateway">
                A WooCommerce client needs a reliable payment gateway or wants to review their current provider.
              </Card>
              <Card title="Shopify Payment Support">
                A Shopify merchant wants payment support, pricing guidance or an alternative payment setup conversation.
              </Card>
              <Card title="Magento Or Custom Ecommerce Project">
                A Magento or custom ecommerce project needs payment processing options, onboarding support or payment
                guidance.
              </Card>
              <Card title="Retail Or Hospitality Referral">
                A retail, hospitality or service client asks about reducing card payment costs or improving their payment
                setup.
              </Card>
              <Card title="Online And In-Person Payments">
                A client needs support with online payments, card machines or integrated payment options across multiple
                sales channels.
              </Card>
              <Card title="Post-Project Recurring Revenue">
                An agency wants to create recurring revenue after a website, ecommerce or digital transformation project
                has been delivered.
              </Card>
              <Card title="Recommend Without Supporting Payments">
                A developer wants to recommend a payment provider without becoming the client&apos;s ongoing payment
                support contact.
              </Card>
            </div>
          </div>
        </section>

        {/* ── 8. How it works ──────────────────────────────────────── */}
        <section className="py-14 sm:py-20 bg-brand-off-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="How Does The Partner Programme Work?">
              Four simple steps from referral to recurring commission.
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                ["Refer A Merchant", "Introduce a merchant who needs payment processing, online payments or payment gateway support."],
                ["We Do The Heavy Lifting", "CanDo manages the payment conversation, pricing, proposals, onboarding and merchant setup."],
                ["Merchant Goes Live", "The merchant starts processing payments through CanDo Payments."],
                [
                  "You Earn Commission",
                  "Receive recurring commission while the merchant remains active and continues processing through CanDo.",
                ],
              ].map(([title, body], i) => (
                <div
                  key={title}
                  className="bg-white border border-brand-light-grey rounded-2xl p-6 shadow-sm h-full"
                  data-testid={`how-step-${i + 1}`}
                >
                  <div className="w-9 h-9 rounded-full bg-brand-aqua text-white font-heading font-bold flex items-center justify-center mb-4">
                    {i + 1}
                  </div>
                  <h3 className="font-heading text-base font-bold text-brand-black mb-2">{title}</h3>
                  <p className="text-sm text-brand-grey leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 9. Earnings potential ────────────────────────────────── */}
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="What Could Your Referrals Be Worth?">
              Recurring commission can build as more referred merchants go live and continue processing through CanDo
              Payments. The example below uses a conservative average of £50 per active merchant per month.
            </SectionHeading>

            {/* Table on sm+, cards on mobile (no horizontal scroll). */}
            <div className="hidden sm:block overflow-hidden rounded-2xl border border-brand-light-grey shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-brand-aqua-light">
                  <tr className="text-left text-brand-black">
                    <th className="px-5 py-3 font-bold">Active Merchants</th>
                    <th className="px-5 py-3 font-bold">Monthly Commission</th>
                    <th className="px-5 py-3 font-bold">Annual Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-light-grey/70">
                  {[
                    ["5", "£250", "£3,000"],
                    ["10", "£500", "£6,000"],
                    ["25", "£1,250", "£15,000"],
                    ["50", "£2,500", "£30,000"],
                  ].map((row) => (
                    <tr key={row[0]} className="text-brand-grey">
                      <td className="px-5 py-3 font-semibold text-brand-black">{row[0]}</td>
                      <td className="px-5 py-3">{row[1]}</td>
                      <td className="px-5 py-3">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="sm:hidden space-y-3">
              {[
                ["5", "£250", "£3,000"],
                ["10", "£500", "£6,000"],
                ["25", "£1,250", "£15,000"],
                ["50", "£2,500", "£30,000"],
              ].map((row) => (
                <div key={row[0]} className="bg-white border border-brand-light-grey rounded-xl p-4 shadow-sm">
                  <p className="text-sm font-bold text-brand-black mb-2">{`${row[0]} active merchants`}</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-brand-grey">Monthly</span>
                    <span className="font-semibold text-brand-black">{row[1]}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-brand-grey">Annual</span>
                    <span className="font-semibold text-brand-black">{row[2]}</span>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-brand-grey leading-relaxed mt-5">
              Illustrative examples only. Actual partner earnings may be higher or lower depending on merchant approval,
              processing volume, transaction mix, pricing, and ongoing merchant activity. Commission is payable only on
              approved merchants who go live and continue processing through CanDo Payments.
            </p>
          </div>
        </section>

        {/* ── 10. Partner earnings calculator ──────────────────────── */}
        <section ref={calcRef} className="py-14 sm:py-20 bg-brand-off-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Estimate Your Potential Partner Earnings">
              Use the calculator to estimate potential monthly and annual commission from active merchant referrals.
            </SectionHeading>
            <PartnerEarningsCalculator applyHref="#partner-application" />
          </div>
        </section>

        {/* ── 11. Supported ecommerce platforms ────────────────────── */}
        <section className="py-14 sm:py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <SectionHeading title="Which Ecommerce Platforms Can Partners Refer?">
              CanDo Payments supports merchants using leading ecommerce platforms, including Shopify, WooCommerce and
              other popular online store systems.
            </SectionHeading>
            <div className="flex flex-wrap justify-center gap-2.5">
              {PLATFORMS.map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center bg-brand-aqua-light/60 border border-brand-aqua/20 rounded-full px-4 py-2 text-sm font-semibold text-brand-black"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── 12. Why partners choose CanDo ────────────────────────── */}
        <section className="py-14 sm:py-20 bg-brand-off-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Why Partners Choose CanDo Payments" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <Card icon={ShoppingBag} title="Ecommerce Payment Support">
                Support for merchants selling through Shopify, WooCommerce and other ecommerce platforms.
              </Card>
              <Card icon={Building2} title="Multiple Acquiring Options">
                Access to solutions tailored around different merchant needs and business types.
              </Card>
              <Card icon={TrendingUp} title="Competitive Merchant Pricing">
                Help clients review their payment costs and access suitable processing options.
              </Card>
              <Card icon={Headset} title="UK-Based Service">
                Real people supporting partner conversations, merchant onboarding and ongoing service.
              </Card>
              <Card icon={Zap} title="Fast Decision Making">
                Direct access to experienced payment specialists.
              </Card>
              <Card icon={Users} title="Experience Across Key Sectors">
                Support for ecommerce, retail, hospitality and service-based merchants.
              </Card>
            </div>
          </div>
        </section>

        {/* ── 13. Trust, support and security ──────────────────────── */}
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Payment Support Partners Can Trust">
              CanDo Payments supports UK merchants with payment processing, ecommerce payment gateways, card machines and
              online payment solutions. Partners can introduce clients knowing that CanDo will manage the payment
              conversation, onboarding and support process with a clear, UK-based team. For ecommerce referrals, CanDo
              can support merchants using popular platforms such as Shopify, WooCommerce, Magento, BigCommerce, OpenCart
              and PrestaShop, subject to merchant requirements and approval.
            </SectionHeading>
            <div className="flex flex-wrap justify-center gap-2.5">
              {[
                "UK-based support",
                "Merchant onboarding guidance",
                "Ecommerce and in-person payment knowledge",
                "Secure payment processing",
                "Support for online and omnichannel businesses",
                "Real partner communication",
              ].map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center gap-1.5 bg-white border border-brand-light-grey rounded-full px-3.5 py-1.5 text-sm text-brand-black"
                >
                  <ShieldCheck size={14} className="text-brand-aqua flex-shrink-0" />{p}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── 14. Social proof — approved factual fallback (spec: use until
            real partner testimonials are approved; no fabricated quotes, no
            Review/AggregateRating schema). Swap for real quotes when ready. ── */}
        <section className="py-14 sm:py-20 bg-brand-off-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Why Partners Work With CanDo" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <Card icon={Headset} title="Direct Access To Payment Specialists">
                Partners can speak directly with the CanDo team when a referral needs payment guidance, pricing support
                or onboarding help.
              </Card>
              <Card icon={Users} title="Support With Merchant Conversations">
                CanDo helps manage the payment conversation so partners do not need to become payment specialists
                themselves.
              </Card>
              <Card icon={ShoppingBag} title="Ecommerce And In-Person Payment Knowledge">
                CanDo supports merchants across ecommerce, retail, hospitality, service businesses and omnichannel
                payment setups.
              </Card>
              <Card icon={Repeat} title="Recurring Commercial Opportunity">
                Partners can earn ongoing commission from successful merchant referrals who go live and continue
                processing through CanDo.
              </Card>
            </div>
          </div>
        </section>

        {/* ── 15. FAQ ──────────────────────────────────────────────── */}
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Frequently Asked Questions" />
            <ResponsiveAccordion items={FAQS} testIdPrefix="partner-faq" />
          </div>
        </section>

        {/* ── 16. Final CTA ────────────────────────────────────────── */}
        <section className="cta-section-bg py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
              Ready To Start Earning Recurring Revenue?
            </h2>
            <p className="text-white/90 leading-relaxed mt-4">
              Join the CanDo Payments Partner Programme and turn existing client relationships into long-term recurring
              income.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
              <a
                href="#partner-application"
                onClick={() => analytics.landingEvent("partner_apply_click")}
                className="inline-flex items-center justify-center gap-2 bg-white text-brand-aqua-dark font-semibold rounded-full px-6 py-3.5 text-sm hover:bg-brand-off-white transition-colors"
                data-testid="final-apply"
                data-measurement-owned="landing"
              >
                Apply For The Partner Programme <ArrowRight size={16} />
              </a>
              <a
                href="#partner-discovery-call"
                onClick={() => analytics.landingEvent("partner_discovery_click")}
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white font-semibold rounded-full px-6 py-3.5 text-sm hover:bg-white/10 transition-colors"
                data-testid="final-discovery"
                data-measurement-owned="landing"
              >
                Book A Discovery Call
              </a>
            </div>
          </div>
        </section>

        {/* ── 17. Application form ──────────────────────────────────── */}
        <section ref={formRef} id="partner-application" className="py-14 sm:py-20 bg-brand-off-white scroll-mt-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Apply For The Partner Programme">
              Complete the short form below and the CanDo Payments team will review your application.
            </SectionHeading>
            <div className="bg-white border border-brand-light-grey rounded-2xl shadow-sm p-6 sm:p-8">
              <PartnerApplicationForm />
            </div>
          </div>
        </section>

        {/* ── 18. Zoho Bookings discovery iframe ────────────────────── */}
        <section
          ref={discoveryRef}
          id="partner-discovery-call"
          className="py-14 sm:py-20 bg-white scroll-mt-20"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading title="Prefer To Speak First?">
              Not sure whether the Partner Programme is the right fit? Book a short discovery call with the CanDo Payments
              team and we&apos;ll talk through your clients, referral opportunities and how the programme works.
            </SectionHeading>
            <div className="bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden">
              <iframe
                src={ZOHO_BOOKINGS_URL}
                title="Partner Programme discovery call booking form"
                width="100%"
                height="750"
                style={{ border: 0, display: "block" }}
                allowFullScreen
                loading="lazy"
                onLoad={handleIframeLoad}
                data-testid="partner-zoho-iframe"
              />
            </div>
            <p className="text-center text-sm text-brand-grey mt-4">
              {"Trouble loading the form? "}
              <a
                href={ZOHO_BOOKINGS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.landingEvent("partner_zoho_fallback_click")}
                className="font-semibold text-brand-aqua-dark hover:text-brand-aqua underline underline-offset-2 inline-flex items-center gap-1"
                data-testid="partner-zoho-fallback"
                data-measurement-owned="landing"
              >
                Open the booking form in a new tab <ExternalLink size={13} />
              </a>
            </p>
          </div>
        </section>
      </main>

      {/* ── 19. Minimal footer ──────────────────────────────────────── */}
      <footer className="bg-white border-t border-brand-light-grey py-10" data-testid="partner-landing-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-5">
          <img src={LOGO_URL} alt="CanDo Payments" width="1217" height="165" className="h-6 w-auto" />
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm" aria-label="Legal">
            <Link to="/privacy-policy" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Terms
            </Link>
            <Link to="/contact" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Contact
            </Link>
          </nav>
        </div>
        <p className="text-center text-xs text-brand-grey mt-6 px-4">
          {`© ${new Date().getFullYear()} Can Do Payments Ltd. Company No. 11701057. VAT: GB311835329`}
        </p>
      </footer>

      {/* Sticky mobile Apply CTA — appears after the hero, hidden while the
          calculator / form / iframe are on screen so it never covers them. */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-30 p-3 bg-white/95 backdrop-blur-md border-t border-brand-light-grey transition-transform duration-300 ${
          showSticky ? "translate-y-0" : "translate-y-full"
        }`}
        aria-hidden={!showSticky}
        data-testid="partner-sticky-cta"
      >
        <a
          href="#partner-application"
          onClick={() => analytics.landingEvent("partner_mobile_sticky_apply_click")}
          tabIndex={showSticky ? 0 : -1}
          className="cta-primary text-white font-semibold rounded-full px-6 py-3 text-sm flex items-center justify-center gap-2 w-full"
          data-testid="partner-sticky-apply"
          data-measurement-owned="landing"
        >
          Apply For The Partner Programme <ArrowRight size={16} />
        </a>
      </div>
    </div>
  );
}
